import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layers, Droplets, Scissors } from 'lucide-react';

const TABS = [
  { key: 'care_laying', label: 'Laying', icon: Layers, intro: 'Turf is a living plant — get it down and watered the day it arrives.' },
  { key: 'care_watering', label: 'Watering', icon: Droplets, intro: 'The first month of watering decides how well your lawn roots in.' },
  { key: 'care_mowing', label: 'Mowing & care', icon: Scissors, intro: 'A few habits keep this variety looking its best year round.' },
];

export function CareGuide({ variety }) {
  const available = TABS.filter((t) => variety[t.key]);
  const [active, setActive] = useState(available[0]?.key);
  const ref = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === '#care-guide' && ref.current) setTimeout(() => ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }, [hash, variety.id]);

  if (!available.length) return null;
  const tab = available.find((t) => t.key === active) || available[0];
  const steps = (variety[tab.key] || '').split('\n').map((s) => s.trim()).filter(Boolean);

  return (
    <section id="care-guide" ref={ref} className="mt-16 lg:mt-24 scroll-mt-24" data-testid="care-guide">
      <div className="rounded-3xl p-8 lg:p-12 grid lg:grid-cols-[280px_1fr] gap-8 lg:gap-14" style={{ background: 'var(--s-sand)' }}>
        <div>
          <p className="store-eyebrow mb-3">Lawn care guide</p>
          <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">Laying &amp; caring for your {variety.name.replace(/ DNA Certified/i, '')}</h2>
          <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--s-muted)' }}>Short, practical steps from the farm. Keep this page handy on lay day.</p>
          <div className="flex lg:flex-col gap-2 flex-wrap" role="tablist">
            {available.map((t) => (
              <button key={t.key} role="tab" aria-selected={t.key === tab.key} onClick={() => setActive(t.key)} className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${t.key === tab.key ? 'text-white' : 'bg-white hover:bg-white/70'}`} style={t.key === tab.key ? { background: 'var(--s-green)' } : { color: 'var(--s-green)' }} data-testid={`care-tab-${t.key}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 lg:p-8" data-testid="care-content">
          <p className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><tab.icon className="h-5 w-5" style={{ color: 'var(--s-terra)' }} /> {tab.label}</p>
          <p className="text-sm mb-6" style={{ color: 'var(--s-muted)' }}>{tab.intro}</p>
          <ol className="space-y-4">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-4 text-sm leading-relaxed" data-testid={`care-step-${i}`}>
                <span className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'var(--s-green-3)' }}>{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
